import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Clock, ChevronDown, ChevronUp, Loader2, Plus, Trash2, Link2, Edit2, TrendingUp, Paperclip, X } from "lucide-react";

// Financial fields that are authoritative on the linked invoice (P3).
// Edits to these fields write to the invoice AND to visa_quotes.accounting.
const INVOICE_FINANCIAL_FIELDS = new Set(["payment_status", "amount_received", "date_received", "vat_type"]);

// Map panel display payment_status → invoice API value
const PS_TO_INVOICE = {
  "Not Requested":      "not_requested",
  "Payment Pending":    "payment_pending",
  "Partially Paid":     "partially_paid",
  "Paid":               "paid",
  "Full Refund":        "refunded",
  "Partially Refunded": "partially_paid",
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VAT_OPTIONS = [
  {
    value: "Dutch VAT 21%",
    note: "VAT charged at 21% (Dutch standard rate)",
  },
  {
    value: "Reverse Charge",
    note: "Reverse charge mechanism applies — VAT not charged (Article 196 EU VAT Directive)",
  },
  {
    value: "Outside Scope of Dutch VAT",
    note: "Service outside the scope of Dutch VAT — non-EU customer (Article 44 EU VAT Directive)",
  },
  {
    value: "VAT Exempt",
    note: "VAT exempt — no VAT charged",
  },
];

// ── Payment Schedule ──────────────────────────────────────────────────────────
function PaymentScheduleSection({ quoteId, quote, onUpdate, getAuthHeaders }) {
  const acc = quote?.accounting || {};
  const milestones = acc.payment_schedule || acc.payment_milestones || [];
  const [showSetup, setShowSetup] = useState(false);
  const [setupType, setSetupType] = useState("50_50");
  const [invoiceTotal, setInvoiceTotal] = useState(
    acc.invoice_total != null ? String(acc.invoice_total) : String(quote?.service_fee || "")
  );
  const [submissionDate, setSubmissionDate] = useState(acc.submission_date || "");
  const [settingUp, setSettingUp] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(null);
  const [payForms, setPayForms] = useState({});

  useEffect(() => {
    const a = quote?.accounting || {};
    setInvoiceTotal(a.invoice_total != null ? String(a.invoice_total) : String(quote?.service_fee || ""));
    setSubmissionDate(a.submission_date || "");
  }, [quote]);

  const setupSchedule = async () => {
    setSettingUp(true);
    try {
      await axios.post(
        `${API}/visa-quotes/${quoteId}/accounting/milestones/setup`,
        { type: setupType, invoice_total: parseFloat(invoiceTotal) || 0, submission_date: submissionDate },
        { headers: getAuthHeaders() }
      );
      toast.success("Payment schedule created");
      setShowSetup(false);
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error("Setup failed — " + (e.response?.data?.detail || e.message));
    } finally {
      setSettingUp(false);
    }
  };

  const togglePayForm = (id) =>
    setPayForms(f => ({ ...f, [id]: f[id] ? null : { paid_date: "", paid_amount: "", payment_method: "", payment_reference: "" } }));

  const handlePayFormChange = (id, field, value) =>
    setPayForms(f => ({ ...f, [id]: { ...(f[id] || {}), [field]: value } }));

  const markPaid = async (milestone) => {
    const form = payForms[milestone.id] || {};
    setMarkingPaid(milestone.id);
    try {
      await axios.post(
        `${API}/visa-quotes/${quoteId}/accounting/milestones/${milestone.id}/mark-paid`,
        {
          paid_date: form.paid_date || null,
          paid_amount: form.paid_amount ? parseFloat(form.paid_amount) : null,
          payment_method: form.payment_method || null,
          payment_reference: form.payment_reference || null,
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Payment recorded ✓");
      setPayForms(f => ({ ...f, [milestone.id]: null }));
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error("Failed — " + (e.response?.data?.detail || e.message));
    } finally {
      setMarkingPaid(null);
    }
  };

  const invoiceTotalNum = parseFloat(acc.invoice_total || 0);
  const balanceDue = acc.balance_due != null ? parseFloat(acc.balance_due) : invoiceTotalNum;
  const amountReceived = parseFloat(acc.amount_received || 0);

  return (
    <div className="border-t border-slate-100 pt-4 mt-2">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-slate-600 uppercase tracking-widest">Payment Schedule</span>
        {milestones.length === 0 && (
          <Button size="sm" variant="outline" onClick={() => setShowSetup(s => !s)}
            className="text-xs h-7 border-violet-300 text-violet-700 hover:bg-violet-50">
            {showSetup ? "Cancel" : "+ Set up schedule"}
          </Button>
        )}
      </div>

      {showSetup && milestones.length === 0 && (
        <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 space-y-3 mb-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Invoice Total (€)</Label>
              <Input type="number" step="0.01" value={invoiceTotal}
                onChange={e => setInvoiceTotal(e.target.value)}
                className="mt-1 h-8 text-sm font-mono" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Payment Plan</Label>
              <Select value={setupType} onValueChange={setSetupType}>
                <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="50_50">50% advance / 50% after submission</SelectItem>
                  <SelectItem value="full_upfront">Full payment upfront</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {setupType === "50_50" && (
            <div>
              <Label className="text-xs text-slate-500">Expected Submission Date (optional)</Label>
              <Input type="date" value={submissionDate}
                onChange={e => setSubmissionDate(e.target.value)}
                className="mt-1 h-8 text-sm" />
            </div>
          )}
          <Button size="sm" onClick={setupSchedule} disabled={settingUp}
            className="w-full bg-violet-600 hover:bg-violet-700 text-white text-xs h-8">
            {settingUp ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Creating...</> : "Create Payment Schedule"}
          </Button>
        </div>
      )}

      {milestones.length > 0 && (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2 mb-2">
            <div className="bg-slate-50 rounded p-2 text-center">
              <p className="text-xs text-slate-400">Invoice Total</p>
              <p className="text-sm font-semibold font-mono">€{invoiceTotalNum.toFixed(2)}</p>
            </div>
            <div className="bg-green-50 rounded p-2 text-center">
              <p className="text-xs text-green-500">Received</p>
              <p className="text-sm font-semibold font-mono text-green-700">€{amountReceived.toFixed(2)}</p>
            </div>
            <div className={`rounded p-2 text-center ${balanceDue > 0 ? "bg-amber-50" : "bg-green-50"}`}>
              <p className={`text-xs ${balanceDue > 0 ? "text-amber-500" : "text-green-500"}`}>Balance Due</p>
              <p className={`text-sm font-semibold font-mono ${balanceDue > 0 ? "text-amber-700" : "text-green-700"}`}>
                €{balanceDue.toFixed(2)}
              </p>
            </div>
          </div>

          {milestones.map((m) => {
            const isPaid = m.status === "paid";
            const formOpen = !!payForms[m.id];
            return (
              <div key={m.id} className={`rounded-lg border p-3 ${isPaid ? "bg-green-50 border-green-200" : "bg-white border-slate-200"}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {isPaid
                        ? <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                        : <Clock className="w-4 h-4 text-amber-500 shrink-0" />}
                      <span className="text-sm font-medium text-slate-700">{m.label}</span>
                      <Badge className={`text-xs ${isPaid ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                        {isPaid ? "Paid" : "Pending"}
                      </Badge>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500">
                      <span className="font-mono font-medium text-slate-700">€{Number(m.amount).toFixed(2)}</span>
                      {m.percentage != null && <span>{m.percentage}%</span>}
                      {m.due_date && <span>Due: {m.due_date}</span>}
                      {isPaid && m.paid_date && <span className="text-green-600">Paid: {m.paid_date}</span>}
                      {isPaid && m.payment_method && <span>{m.payment_method}</span>}
                      {isPaid && m.payment_reference && <span className="font-mono">{m.payment_reference}</span>}
                    </div>
                  </div>
                  {!isPaid && (
                    <button onClick={() => togglePayForm(m.id)}
                      className="text-xs text-violet-600 hover:text-violet-800 shrink-0 flex items-center gap-1">
                      Record Payment
                      {formOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                  )}
                </div>

                {formOpen && !isPaid && (
                  <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-slate-400">Date Received</Label>
                        <Input type="date" value={payForms[m.id]?.paid_date || ""}
                          onChange={e => handlePayFormChange(m.id, "paid_date", e.target.value)}
                          className="mt-0.5 h-7 text-xs" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-400">Amount (€)</Label>
                        <Input type="number" step="0.01" placeholder={String(m.amount)}
                          value={payForms[m.id]?.paid_amount || ""}
                          onChange={e => handlePayFormChange(m.id, "paid_amount", e.target.value)}
                          className="mt-0.5 h-7 text-xs font-mono" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-400">Payment Method</Label>
                        <Input placeholder="Tikkie, Bank transfer…"
                          value={payForms[m.id]?.payment_method || ""}
                          onChange={e => handlePayFormChange(m.id, "payment_method", e.target.value)}
                          className="mt-0.5 h-7 text-xs" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-400">Reference</Label>
                        <Input placeholder="Transaction ref…"
                          value={payForms[m.id]?.payment_reference || ""}
                          onChange={e => handlePayFormChange(m.id, "payment_reference", e.target.value)}
                          className="mt-0.5 h-7 text-xs font-mono" />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => markPaid(m)} disabled={markingPaid === m.id}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs h-7">
                        {markingPaid === m.id
                          ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Saving...</>
                          : <><CheckCircle className="w-3 h-3 mr-1" />Confirm Paid</>}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => togglePayForm(m.id)}
                        className="text-xs h-7">Cancel</Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Related Expenses ──────────────────────────────────────────────────────────
function RelatedExpensesSection({ quoteId, quote, onUpdate, getAuthHeaders }) {
  const expenses = (quote?.accounting?.related_expenses) || [];
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ description: "", supplier: "", amount: "", currency: "EUR", paid_by_customer: false });
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const CURRENCIES = ["EUR", "GBP", "USD", "AED"];

  const addExpense = async () => {
    if (!form.description || !form.amount) { toast.error("Description and amount are required"); return; }
    setAdding(true);
    try {
      await axios.post(
        `${API}/visa-quotes/${quoteId}/accounting/expenses`,
        { ...form, amount: parseFloat(form.amount) },
        { headers: getAuthHeaders() }
      );
      toast.success("Expense added");
      setForm({ description: "", supplier: "", amount: "", currency: "EUR", paid_by_customer: false });
      setShowForm(false);
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error("Failed — " + (e.response?.data?.detail || e.message));
    } finally {
      setAdding(false);
    }
  };

  const removeExpense = async (id) => {
    setDeleting(id);
    try {
      await axios.delete(`${API}/visa-quotes/${quoteId}/accounting/expenses/${id}`, { headers: getAuthHeaders() });
      toast.success("Expense removed");
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error("Failed — " + (e.response?.data?.detail || e.message));
    } finally {
      setDeleting(null);
    }
  };

  const tbfExpenses = expenses.filter(e => !e.paid_by_customer);
  const customerExpenses = expenses.filter(e => e.paid_by_customer);
  const tbfTotal = tbfExpenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);

  return (
    <div className="border-t border-slate-100 pt-4 mt-2">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-slate-600 uppercase tracking-widest">Related Expenses</span>
        <Button size="sm" variant="outline" onClick={() => setShowForm(s => !s)}
          className="text-xs h-7 border-slate-300 text-slate-600 hover:bg-slate-50">
          {showForm ? "Cancel" : <><Plus className="w-3 h-3 mr-1" />Add Expense</>}
        </Button>
      </div>

      {showForm && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2 mb-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <Label className="text-xs text-slate-500">Description *</Label>
              <Input value={form.description} placeholder="VFS appointment fee, translation…"
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                className="mt-0.5 h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Supplier</Label>
              <Input value={form.supplier} placeholder="VFS, UKVI…"
                onChange={e => setForm(f => ({ ...f, supplier: e.target.value }))}
                className="mt-0.5 h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Amount *</Label>
              <div className="flex gap-1 mt-0.5">
                <Input type="number" step="0.01" value={form.amount} placeholder="0.00"
                  onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                  className="h-8 text-sm font-mono flex-1" />
                <Select value={form.currency} onValueChange={v => setForm(f => ({ ...f, currency: v }))}>
                  <SelectTrigger className="h-8 text-xs w-20"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={form.paid_by_customer}
              onChange={e => setForm(f => ({ ...f, paid_by_customer: e.target.checked }))}
              className="rounded" />
            Paid directly by customer (not TBF cost)
          </label>
          <Button size="sm" onClick={addExpense} disabled={adding}
            className="w-full bg-slate-700 hover:bg-slate-800 text-white text-xs h-7">
            {adding ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Adding...</> : "Add Expense"}
          </Button>
        </div>
      )}

      {expenses.length === 0 && !showForm && (
        <p className="text-xs text-slate-400 italic">No related expenses recorded</p>
      )}

      {expenses.length > 0 && (
        <div className="space-y-1.5">
          {expenses.map(exp => (
            <div key={exp.id} className="flex items-center gap-2 py-1.5 px-2 rounded-lg bg-slate-50 border border-slate-100">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-700 truncate">{exp.description}</p>
                <p className="text-xs text-slate-400">{exp.supplier || "—"}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs font-mono font-semibold text-slate-700">
                  {exp.currency === "EUR" ? "€" : exp.currency}{Number(exp.amount).toFixed(2)}
                </p>
                {exp.paid_by_customer && (
                  <span className="text-xs text-blue-500">Customer pays</span>
                )}
              </div>
              <button onClick={() => removeExpense(exp.id)} disabled={deleting === exp.id}
                className="p-1 text-red-400 hover:text-red-600 shrink-0">
                {deleting === exp.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
              </button>
            </div>
          ))}

          {tbfExpenses.length > 0 && (
            <div className="flex items-center justify-between pt-1 px-2 text-xs font-medium text-slate-600 border-t border-slate-100">
              <span>TBF Total Expenses</span>
              <span className="font-mono">€{tbfTotal.toFixed(2)}</span>
            </div>
          )}
          {customerExpenses.length > 0 && (
            <p className="text-xs text-slate-400 px-2">{customerExpenses.length} item(s) paid directly by customer — not included in TBF cost</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Supplier Cost Items ───────────────────────────────────────────────────────
const EMPTY_COST_ITEM = { description: "", supplier: "", amount: "", paid_date: "", payment_method: "", reference: "" };

function CostItemsSection({ quoteId, quote, onUpdate, getAuthHeaders }) {
  const costItems = (quote?.accounting?.cost_items) || [];
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState(EMPTY_COST_ITEM);
  const [adding, setAdding]     = useState(false);
  const [editId, setEditId]     = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving]     = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);

  const addItem = async () => {
    if (!form.description || !form.amount) { toast.error("Description and amount are required"); return; }
    setAdding(true);
    try {
      await axios.post(
        `${API}/visa-quotes/${quoteId}/accounting/cost-items`,
        { ...form, amount: parseFloat(form.amount) },
        { headers: getAuthHeaders() }
      );
      toast.success("Cost item added");
      setForm(EMPTY_COST_ITEM);
      setShowForm(false);
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error("Failed — " + (e.response?.data?.detail || e.message));
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (item) => {
    setEditId(item.id);
    setEditForm({
      description:    item.description    || "",
      supplier:       item.supplier       || "",
      amount:         item.amount != null ? String(item.amount) : "",
      paid_date:      item.paid_date      || "",
      payment_method: item.payment_method || "",
      reference:      item.reference      || "",
    });
  };

  const saveEdit = async (id) => {
    setSaving(id);
    try {
      await axios.patch(
        `${API}/visa-quotes/${quoteId}/accounting/cost-items/${id}`,
        { ...editForm, amount: parseFloat(editForm.amount) || 0 },
        { headers: getAuthHeaders() }
      );
      toast.success("Updated ✓");
      setEditId(null);
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error("Failed — " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(null);
    }
  };

  const removeItem = async (id) => {
    setDeleting(id);
    try {
      await axios.delete(`${API}/visa-quotes/${quoteId}/accounting/cost-items/${id}`, { headers: getAuthHeaders() });
      toast.success("Removed");
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error("Failed — " + (e.response?.data?.detail || e.message));
    } finally {
      setDeleting(null);
    }
  };

  const uploadAttachment = async (itemId, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10 MB)"); return; }
    setUploadingId(itemId);
    try {
      const base64 = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = e => res(e.target.result.split(",")[1]);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      const up = await axios.post(`${API}/uploads/receipt`, {
        file_data: base64, filename: file.name,
        mime_type: file.type, folder: "visa-supplier-receipts", invoice_number: `${quoteId}-item-${itemId}`,
      }, { headers: getAuthHeaders() });
      const url = up.data.url;
      if (!url) { toast.error("Upload failed — storage unavailable"); return; }
      await axios.patch(`${API}/visa-quotes/${quoteId}/accounting/cost-items/${itemId}`,
        { receipt_url: url }, { headers: getAuthHeaders() });
      toast.success("Attachment saved");
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error("Upload failed — " + (e.response?.data?.detail || e.message));
    } finally {
      setUploadingId(null);
    }
  };

  const removeAttachment = async (itemId) => {
    try {
      await axios.patch(`${API}/visa-quotes/${quoteId}/accounting/cost-items/${itemId}`,
        { receipt_url: null }, { headers: getAuthHeaders() });
      toast.success("Attachment removed");
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error("Failed — " + (e.response?.data?.detail || e.message));
    }
  };

  const totalCost    = costItems.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const amtReceived  = parseFloat(quote?.accounting?.amount_received || 0);
  const bankFee      = parseFloat(quote?.accounting?.bank_fee || 0);
  const grossProfit  = amtReceived ? amtReceived - totalCost - bankFee : null;

  return (
    <div className="border-t border-slate-100 pt-4 mt-2">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-slate-600 uppercase tracking-widest">Supplier Costs</span>
        <Button size="sm" variant="outline" onClick={() => setShowForm(s => !s)}
          className="text-xs h-7 border-orange-300 text-orange-700 hover:bg-orange-50">
          {showForm ? "Cancel" : <><Plus className="w-3 h-3 mr-1" />Add Cost</>}
        </Button>
      </div>

      {showForm && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-2 mb-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <Label className="text-xs text-slate-500">Description *</Label>
              <Input value={form.description} placeholder="Visa application fee, courier…"
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                className="mt-0.5 h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Supplier</Label>
              <Input value={form.supplier} placeholder="VFS, UKVI, DHL…"
                onChange={e => setForm(f => ({ ...f, supplier: e.target.value }))}
                className="mt-0.5 h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Amount (€) *</Label>
              <Input type="number" step="0.01" value={form.amount} placeholder="0.00"
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                className="mt-0.5 h-8 text-sm font-mono" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Date Paid</Label>
              <Input type="date" value={form.paid_date}
                onChange={e => setForm(f => ({ ...f, paid_date: e.target.value }))}
                className="mt-0.5 h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Payment Method</Label>
              <Input value={form.payment_method} placeholder="AMEX, Bank transfer…"
                onChange={e => setForm(f => ({ ...f, payment_method: e.target.value }))}
                className="mt-0.5 h-8 text-sm" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-slate-500">Reference</Label>
              <Input value={form.reference} placeholder="Transaction ref, receipt #…"
                onChange={e => setForm(f => ({ ...f, reference: e.target.value }))}
                className="mt-0.5 h-8 text-sm font-mono" />
            </div>
          </div>
          <Button size="sm" onClick={addItem} disabled={adding}
            className="w-full bg-orange-600 hover:bg-orange-700 text-white text-xs h-7">
            {adding ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Adding...</> : "Add Supplier Cost"}
          </Button>
        </div>
      )}

      {costItems.length === 0 && !showForm && (
        <p className="text-xs text-slate-400 italic">No supplier costs recorded</p>
      )}

      {costItems.length > 0 && (
        <div className="space-y-1.5">
          {costItems.map(item => (
            <div key={item.id} className="rounded-lg border border-slate-100 bg-white">
              {editId === item.id ? (
                <div className="p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="col-span-2">
                      <Label className="text-xs text-slate-500">Description</Label>
                      <Input value={editForm.description}
                        onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                        className="mt-0.5 h-7 text-xs" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Supplier</Label>
                      <Input value={editForm.supplier}
                        onChange={e => setEditForm(f => ({ ...f, supplier: e.target.value }))}
                        className="mt-0.5 h-7 text-xs" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Amount (€)</Label>
                      <Input type="number" step="0.01" value={editForm.amount}
                        onChange={e => setEditForm(f => ({ ...f, amount: e.target.value }))}
                        className="mt-0.5 h-7 text-xs font-mono" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Date Paid</Label>
                      <Input type="date" value={editForm.paid_date}
                        onChange={e => setEditForm(f => ({ ...f, paid_date: e.target.value }))}
                        className="mt-0.5 h-7 text-xs" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Payment Method</Label>
                      <Input value={editForm.payment_method}
                        onChange={e => setEditForm(f => ({ ...f, payment_method: e.target.value }))}
                        className="mt-0.5 h-7 text-xs" />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs text-slate-500">Reference</Label>
                      <Input value={editForm.reference}
                        onChange={e => setEditForm(f => ({ ...f, reference: e.target.value }))}
                        className="mt-0.5 h-7 text-xs font-mono" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => saveEdit(item.id)} disabled={saving === item.id}
                      className="flex-1 bg-orange-600 hover:bg-orange-700 text-white text-xs h-7">
                      {saving === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditId(null)} className="text-xs h-7">Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 py-1.5 px-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-700 truncate">{item.description}</p>
                    <p className="text-xs text-slate-400 truncate">
                      {item.supplier || ""}
                      {item.paid_date && <span className="ml-1.5">{item.paid_date}</span>}
                      {item.payment_method && <span className="ml-1.5">{item.payment_method}</span>}
                      {item.reference && <span className="ml-1.5 font-mono">{item.reference}</span>}
                      {item.receipt_url && (
                        <a href={item.receipt_url} target="_blank" rel="noopener noreferrer"
                          className="ml-1.5 text-blue-500 hover:underline inline-flex items-center gap-0.5">
                          <Paperclip className="w-2.5 h-2.5" />receipt
                        </a>
                      )}
                    </p>
                  </div>
                  <span className="text-xs font-mono font-semibold text-slate-700 shrink-0">
                    €{Number(item.amount || 0).toFixed(2)}
                  </span>
                  {uploadingId === item.id ? (
                    <Loader2 className="w-3 h-3 animate-spin text-slate-400 shrink-0" />
                  ) : (
                    <label className="p-1 text-slate-300 hover:text-orange-500 cursor-pointer shrink-0"
                      title={item.receipt_url ? "Replace attachment" : "Attach receipt"}>
                      <Paperclip className="w-3 h-3" />
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
                        onChange={e => { if (e.target.files[0]) uploadAttachment(item.id, e.target.files[0]); e.target.value = ""; }} />
                    </label>
                  )}
                  {item.receipt_url && (
                    <button onClick={() => removeAttachment(item.id)}
                      className="p-1 text-slate-300 hover:text-red-500 shrink-0"
                      title="Remove attachment">
                      <X className="w-3 h-3" />
                    </button>
                  )}
                  <button onClick={() => startEdit(item)}
                    className="p-1 text-slate-400 hover:text-orange-600 shrink-0">
                    <Edit2 className="w-3 h-3" />
                  </button>
                  <button onClick={() => removeItem(item.id)} disabled={deleting === item.id}
                    className="p-1 text-red-400 hover:text-red-600 shrink-0">
                    {deleting === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  </button>
                </div>
              )}
            </div>
          ))}

          <div className="bg-orange-50 border border-orange-100 rounded-lg p-2.5 mt-1 space-y-1">
            <div className="flex items-center justify-between text-xs font-medium text-orange-800">
              <span>Total Supplier Cost</span>
              <span className="font-mono">€{totalCost.toFixed(2)}</span>
            </div>
            {amtReceived > 0 && (
              <>
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>Revenue Received</span>
                  <span className="font-mono">€{amtReceived.toFixed(2)}</span>
                </div>
                {bankFee > 0 && (
                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span>Bank Fee</span>
                    <span className="font-mono">−€{bankFee.toFixed(2)}</span>
                  </div>
                )}
                <div className={`flex items-center justify-between text-xs font-semibold border-t border-orange-200 pt-1 mt-1 ${grossProfit >= 0 ? "text-green-700" : "text-red-600"}`}>
                  <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3" />Gross Profit</span>
                  <span className="font-mono">€{(grossProfit || 0).toFixed(2)}</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────
export default function VisaAccountingPanel({ quoteId, quote, onUpdate }) {
  const { getAuthHeaders } = useAuth();

  const acc = quote?.accounting || {};
  const defaultItemType = quote?.destination_country
    ? `${quote.destination_country} Visa Assistance`
    : "Visa Assistance";

  // Linked invoice — fetched on mount; financial field edits write here (P3).
  const [linkedInvoice, setLinkedInvoice] = useState(null);

  useEffect(() => {
    if (!quoteId) return;
    axios
      .get(`${API}/invoices`, {
        params: { source_type: "visa_quote", source_id: quoteId },
        headers: getAuthHeaders(),
      })
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : [];
        const active = list
          .filter(i => i.invoice_status !== "cancelled")
          .sort((a, b) => (b.invoice_number || "").localeCompare(a.invoice_number || ""));
        setLinkedInvoice(active[0] || null);
      })
      .catch(() => {});
  }, [quoteId]);

  const [fields, setFields] = useState({
    invoice_number:     acc.invoice_number     || "",
    invoice_date:       acc.invoice_date       || "",
    item_type:          acc.item_type          || defaultItemType,
    vat_type:           acc.vat_type           || "Outside Scope of Dutch VAT",
    vat_note:           acc.vat_note           || "",
    quarter:            acc.quarter            || "",
    customer_country:   acc.customer_country   || "",
    residence_country:  acc.residence_country  || "",
    eu_status:          acc.eu_status          || "Non-EU",
    supplier_name:      acc.supplier_name      || "",
    supplier_reference: acc.supplier_reference || "",
    payment_mode:       acc.payment_mode       || "",
    supplier_paid_date: acc.supplier_paid_date || "",
    payment_status:     acc.payment_status     || "Pending",
    date_received:      acc.date_received      || "",
    amount_received:    acc.amount_received    != null ? String(acc.amount_received) : "",
    bank_fee:           acc.bank_fee           != null ? String(acc.bank_fee)        : "0",
    comment:            acc.comment            || "",
  });

  const [saving, setSaving] = useState({});

  useEffect(() => {
    const a = quote?.accounting || {};
    const itemType = a.item_type || (quote?.destination_country ? `${quote.destination_country} Visa Assistance` : "Visa Assistance");
    setFields({
      invoice_number:     a.invoice_number     || "",
      invoice_date:       a.invoice_date       || "",
      item_type:          itemType,
      vat_type:           a.vat_type           || "Outside Scope of Dutch VAT",
      vat_note:           a.vat_note           || "",
      quarter:            a.quarter            || "",
      customer_country:   a.customer_country   || "",
      residence_country:  a.residence_country  || "",
      eu_status:          a.eu_status          || "Non-EU",
      supplier_name:      a.supplier_name      || "",
      supplier_reference: a.supplier_reference || "",
      payment_mode:       a.payment_mode       || "",
      supplier_paid_date: a.supplier_paid_date || "",
      payment_status:     a.payment_status     || "Pending",
      date_received:      a.date_received      || "",
      amount_received:    a.amount_received    != null ? String(a.amount_received) : "",
      bank_fee:           a.bank_fee           != null ? String(a.bank_fee)        : "0",
      comment:            a.comment            || "",
    });
  }, [quote]);

  const saveField = async (fieldName, value) => {
    setSaving(s => ({ ...s, [fieldName]: true }));
    try {
      const accPayload = { [fieldName]: value };
      if (fieldName === "invoice_date" && value) {
        const m = new Date(value).getMonth();
        accPayload.quarter = `Q${Math.floor(m / 3) + 1}`;
        setFields(f => ({ ...f, quarter: accPayload.quarter }));
      }

      // Financial fields → write to invoice first (P3: invoice is source of truth)
      if (INVOICE_FINANCIAL_FIELDS.has(fieldName) && linkedInvoice?.id) {
        let invoiceValue = value;
        if (fieldName === "payment_status") invoiceValue = PS_TO_INVOICE[value] ?? value;
        if (fieldName === "amount_received") invoiceValue = parseFloat(value) || 0;
        await axios.patch(
          `${API}/invoices/${linkedInvoice.id}`,
          { [fieldName]: invoiceValue },
          { headers: getAuthHeaders() }
        );
      }

      // Always keep visa_quotes.accounting in sync for panel display
      await axios.patch(
        `${API}/visa-quotes/${quoteId}/accounting`,
        accPayload,
        { headers: getAuthHeaders() }
      );
      toast.success("Saved ✓");
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error("Save failed — " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(s => ({ ...s, [fieldName]: false }));
    }
  };

  const handleChange = (field, value) => setFields(f => ({ ...f, [field]: value }));

  const handleBlur = (field) => {
    const value = fields[field];
    const original = (quote?.accounting || {})[field];
    if (String(value ?? "") !== String(original ?? "")) {
      saveField(field, value);
    }
  };

  const handleSelectChange = (field, value) => {
    const updates = { [field]: value };
    if (field === "vat_type") {
      const opt = VAT_OPTIONS.find(o => o.value === value);
      if (opt) {
        updates.vat_note = opt.note;
        setFields(f => ({ ...f, ...updates }));
        setSaving(s => ({ ...s, vat_type: true, vat_note: true }));
        const accPatch = { vat_type: value, vat_note: opt.note };
        const saves = [
          axios.patch(`${API}/visa-quotes/${quoteId}/accounting`, accPatch, { headers: getAuthHeaders() }),
        ];
        if (linkedInvoice?.id) {
          saves.push(
            axios.patch(`${API}/invoices/${linkedInvoice.id}`, { vat_type: value }, { headers: getAuthHeaders() })
          );
        }
        Promise.all(saves)
          .then(() => { toast.success("Saved ✓"); if (onUpdate) onUpdate(); })
          .catch(e => toast.error("Save failed — " + (e.response?.data?.detail || e.message)))
          .finally(() => setSaving(s => ({ ...s, vat_type: false, vat_note: false })));
        return;
      }
    }
    if (field === "eu_status") {
      const autoVatType = value === "EU" ? "Reverse Charge" : "Outside Scope of Dutch VAT";
      const autoNote = VAT_OPTIONS.find(o => o.value === autoVatType)?.note || "";
      updates.vat_type = autoVatType;
      updates.vat_note = autoNote;
      setFields(f => ({ ...f, ...updates }));
      setSaving(s => ({ ...s, eu_status: true }));
      const accPatch = { eu_status: value, vat_type: autoVatType, vat_note: autoNote };
      const saves = [
        axios.patch(`${API}/visa-quotes/${quoteId}/accounting`, accPatch, { headers: getAuthHeaders() }),
      ];
      if (linkedInvoice?.id) {
        saves.push(
          axios.patch(`${API}/invoices/${linkedInvoice.id}`, { vat_type: autoVatType }, { headers: getAuthHeaders() })
        );
      }
      Promise.all(saves)
        .then(() => { toast.success("Saved ✓"); if (onUpdate) onUpdate(); })
        .catch(e => toast.error("Save failed — " + (e.response?.data?.detail || e.message)))
        .finally(() => setSaving(s => ({ ...s, eu_status: false })));
      return;
    }
    setFields(f => ({ ...f, ...updates }));
    saveField(field, value);
  };

  return (
    <div className="space-y-4">

      {/* Invoice numbers */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-500">Quote Reference</Label>
          <Input value={quote?.quote_number || "—"} readOnly className="mt-1 h-9 bg-slate-50 font-mono text-sm" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Invoice Number</Label>
          <Input value={fields.invoice_number} readOnly className="mt-1 h-9 bg-slate-50 font-mono text-sm" />
        </div>
      </div>

      {/* Invoice sync status */}
      {linkedInvoice ? (
        <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5">
          <Link2 className="w-3 h-3 shrink-0" />
          <span>Financial fields synced to invoice <span className="font-mono font-semibold">{linkedInvoice.invoice_number}</span> — changes here update the invoice and VAT report.</span>
        </div>
      ) : (
        <div className="flex items-center gap-1.5 text-xs text-slate-400 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5">
          <Link2 className="w-3 h-3 shrink-0" />
          <span>No linked invoice found — changes saved to accounting record only.</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        {/* Invoice Date */}
        <div>
          <Label className="text-xs text-slate-500">Invoice Date</Label>
          <Input type="date" value={fields.invoice_date}
            onChange={e => handleChange("invoice_date", e.target.value)}
            onBlur={() => handleBlur("invoice_date")}
            className="mt-1 h-9 text-sm" />
        </div>

        {/* Quarter */}
        <div>
          <Label className="text-xs text-slate-500">Quarter</Label>
          <Select value={fields.quarter} onValueChange={v => handleSelectChange("quarter", v)}>
            <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue placeholder="Auto" /></SelectTrigger>
            <SelectContent>
              {["Q1", "Q2", "Q3", "Q4"].map(q => <SelectItem key={q} value={q}>{q}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Customer Location */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">Customer / VAT Details</p>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-slate-500">Customer Country</Label>
            <Input value={fields.customer_country} placeholder="UAE, UK…"
              onChange={e => handleChange("customer_country", e.target.value)}
              onBlur={() => handleBlur("customer_country")}
              className="mt-1 h-9 text-sm" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Residence Country</Label>
            <Input value={fields.residence_country} placeholder="Country of residence"
              onChange={e => handleChange("residence_country", e.target.value)}
              onBlur={() => handleBlur("residence_country")}
              className="mt-1 h-9 text-sm" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">EU Status</Label>
            <Select value={fields.eu_status} onValueChange={v => handleSelectChange("eu_status", v)}>
              <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="EU">EU</SelectItem>
                <SelectItem value="Non-EU">Non-EU</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* VAT Type */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-500">VAT Treatment</Label>
          <Select value={fields.vat_type} onValueChange={v => handleSelectChange("vat_type", v)}>
            <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {VAT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.value}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-500">Payment Status</Label>
          <Select value={fields.payment_status} onValueChange={v => handleSelectChange("payment_status", v)}>
            <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["Not Requested", "Payment Pending", "Partially Paid", "Paid", "Full Refund", "Partially Refunded"].map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* VAT Note */}
      <div>
        <Label className="text-xs text-slate-500">VAT Note (printed on invoice)</Label>
        <Input value={fields.vat_note}
          placeholder="Auto-populated when you select VAT treatment"
          onChange={e => handleChange("vat_note", e.target.value)}
          onBlur={() => handleBlur("vat_note")}
          className="mt-1 h-9 text-sm" />
      </div>

      {/* Item Type */}
      <div>
        <Label className="text-xs text-slate-500">Item Description</Label>
        <Input value={fields.item_type}
          onChange={e => handleChange("item_type", e.target.value)}
          onBlur={() => handleBlur("item_type")}
          className="mt-1 h-9 text-sm" />
      </div>

      {/* Supplier */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-500">Supplier Name</Label>
          <Input value={fields.supplier_name} placeholder="VFS, UKVI…"
            onChange={e => handleChange("supplier_name", e.target.value)}
            onBlur={() => handleBlur("supplier_name")}
            className="mt-1 h-9 text-sm" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Supplier Reference #</Label>
          <Input value={fields.supplier_reference} placeholder="Application reference"
            onChange={e => handleChange("supplier_reference", e.target.value)}
            onBlur={() => handleBlur("supplier_reference")}
            className="mt-1 h-9 text-sm font-mono" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Paid With</Label>
          <Input value={fields.payment_mode} placeholder="Tikkie, Bank Transfer, AMEX…"
            onChange={e => handleChange("payment_mode", e.target.value)}
            onBlur={() => handleBlur("payment_mode")}
            className="mt-1 h-9 text-sm" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Date Paid to Supplier</Label>
          <Input type="date" value={fields.supplier_paid_date}
            onChange={e => handleChange("supplier_paid_date", e.target.value)}
            onBlur={() => handleBlur("supplier_paid_date")}
            className="mt-1 h-9 text-sm" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Date Received from Customer</Label>
          <Input type="date" value={fields.date_received}
            onChange={e => handleChange("date_received", e.target.value)}
            onBlur={() => handleBlur("date_received")}
            className="mt-1 h-9 text-sm" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Amount Received (€)</Label>
          <Input type="number" step="0.01" value={fields.amount_received} placeholder="0.00"
            onChange={e => handleChange("amount_received", e.target.value)}
            onBlur={() => handleBlur("amount_received")}
            className="mt-1 h-9 text-sm font-mono" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Bank Fee (€)</Label>
          <Input type="number" step="0.01" value={fields.bank_fee} placeholder="0.00"
            onChange={e => handleChange("bank_fee", e.target.value)}
            onBlur={() => handleBlur("bank_fee")}
            className="mt-1 h-9 text-sm font-mono" />
        </div>
      </div>

      {/* Comment */}
      <div>
        <Label className="text-xs text-slate-500">Internal Note</Label>
        <Input value={fields.comment} placeholder="Optional internal note"
          onChange={e => handleChange("comment", e.target.value)}
          onBlur={() => handleBlur("comment")}
          className="mt-1 h-9 text-sm" />
      </div>

      {/* Payment Schedule */}
      <PaymentScheduleSection
        quoteId={quoteId}
        quote={quote}
        onUpdate={onUpdate}
        getAuthHeaders={getAuthHeaders}
      />

      {/* Supplier Cost Items */}
      <CostItemsSection
        quoteId={quoteId}
        quote={quote}
        onUpdate={onUpdate}
        getAuthHeaders={getAuthHeaders}
      />

      {/* Related Expenses */}
      <RelatedExpensesSection
        quoteId={quoteId}
        quote={quote}
        onUpdate={onUpdate}
        getAuthHeaders={getAuthHeaders}
      />
    </div>
  );
}
